import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Col, Container, Form, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { type TableColumn } from 'react-data-table-component'
import classNames from 'classnames'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import {
  useGetWalletQuery,
  useGetLedgerQuery,
  useGetLotsQuery,
  useLazyGetBillingMetricsRecordQuery,
  useUpdateWalletConfigMutation,
} from '../../store/wallet/wallet'
import { useGetBillingCustomerQuery } from '../../store/plan/plan'
import { useGetAllUsersQuery } from '../../store/user/user'
import { CROSS_CLIENT_QUERY_WALLET, type BillingMetricsRecord, type LedgerEntry, type WalletConfig } from '../../models/Wallet'
import { type CreditConfig, type PaidCreditTier } from '../../models/Plan'
import { type User } from '../../models/User'
import { ZocksDataTable } from '../../components/DataTable/ZocksDataTable'
import { Modal } from '../../components/Modal/Modal'
import { Button } from '../../components/Button/Button'
import FormattedDate from '../../components/FormattedDate'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../common/integrations'
import { SERVICE_TOKEN_USER_ID } from '../../common/constants'
import { DEFAULT_PAGE_SIZE } from '../../common/constants'
import { PurchaseCreditsModal } from './PurchaseCreditsModal'
import { formatCredits, formatCurrencyFromCents } from './walletFormat'
import { lowestMinQuantity, tierForQuantity } from './creditTiers'

const POLLING_INTERVAL = 15000

const NEGATIVE_BALANCE_MAX_MAGNITUDE = 500

const LEDGER_TYPE_LABELS: Record<string, { label: string; tone: 'credit' | 'debit' }> = {
  grant: { label: 'Grant', tone: 'credit' },
  purchase: { label: 'Purchase', tone: 'credit' },
  refund: { label: 'Refund', tone: 'credit' },
  debit: { label: 'Usage', tone: 'debit' },
  expire: { label: 'Expired', tone: 'debit' },
  transferOut: { label: 'Transfer out', tone: 'debit' },
  transferIn: { label: 'Transfer in', tone: 'credit' },
}

const BMR_TYPE_LABELS: Record<string, string> = {
  meetingSavedToCrm: 'Meeting saved to CRM',
  contactCreatedInCrm: 'Contact created in CRM',
  contactUpdatedInCrm: 'Contact updated in CRM',
  noteSavedToCrm: 'Note saved to CRM',
  aiResultSavedToCrm: 'AI result saved to CRM',
  aiAgentResultSavedToCrm: 'AI agent result saved to CRM',
  tasksSavedToCrm: 'Tasks saved to CRM',
  businessEventCreated: 'Business event',
}

const CONTEXT_KEY_LABELS: Record<string, string> = {
  connector: 'CRM',
  relatedToId: 'Related record',
  resultId: 'Form result',
  contactChangeId: 'Contact change',
  taskIds: 'Tasks',
  exportType: 'Export type',
}

const HIDDEN_CONTEXT_KEYS = new Set(['accountId', 'roomId', 'sessionId', 'aiAgentId', 'aiFeatureId', 'aiFeaturePresetId', 'businessEventId'])

const titleCase = (s: string): string =>
  s
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')

const humanizeKey = (key: string): string => CONTEXT_KEY_LABELS[key] ?? titleCase(key)

const humanizeBmrType = (type: string): string => BMR_TYPE_LABELS[type] ?? titleCase(type)

const UsersByIdContext = createContext<Record<string, User>>({})

interface MeetingLinkProps {
  roomId: string
  sessionId: string
}

const MeetingLink: React.FC<MeetingLinkProps> = ({ roomId, sessionId }) => (
  <Link to={`/workspace/past-meetings/room/${roomId}/session/${sessionId}/`} target='_blank' rel='noreferrer' className='paragraph-small zocks-text-primary'>
    <i className='bi bi-camera-video me-1' />
    View meeting
  </Link>
)

interface ContextDisplayProps {
  context: Record<string, unknown>
}

const ContextDisplay: React.FC<ContextDisplayProps> = ({ context }) => {
  const roomId = typeof context.roomId === 'string' ? context.roomId : null
  const sessionId = typeof context.sessionId === 'string' ? context.sessionId : null
  const showMeetingLink = !!roomId && !!sessionId

  const visibleEntries = Object.entries(context).filter(([key, value]) => {
    if (HIDDEN_CONTEXT_KEYS.has(key)) {
      return false
    }
    if (value === null || value === undefined || value === '') {
      return false
    }
    return true
  })

  if (!showMeetingLink && visibleEntries.length === 0) {
    return <span className='small text-muted'>—</span>
  }

  const renderValue = (key: string, value: unknown): string => {
    if (key === 'connector' && typeof value === 'string') {
      return getIntegrationDisplayName(value as IntegrationServiceId)
    }
    if (key === 'businessEventType' && typeof value === 'string') {
      return titleCase(value)
    }
    if (Array.isArray(value)) {
      return value.join(', ')
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value)
    }
    return String(value)
  }

  return (
    <div className='d-flex flex-column gap-1'>
      {showMeetingLink ? <MeetingLink roomId={roomId!} sessionId={sessionId!} /> : null}
      {visibleEntries.length > 0 ? (
        <Row className='g-2'>
          {visibleEntries.map(([key, value]) => (
            <Col key={key} xs={12} sm={6} md={4}>
              <div className='d-flex gap-1'>
                <span className='small fw-semibold text-muted'>{humanizeKey(key)}:</span>
                <span className='small text-break'>{renderValue(key, value)}</span>
              </div>
            </Col>
          ))}
        </Row>
      ) : null}
    </div>
  )
}

const UserDisplay: React.FC<{ userId: string }> = ({ userId }) => {
  const usersById = useContext(UsersByIdContext)
  if (!userId || userId === SERVICE_TOKEN_USER_ID) {
    return <span className='text-muted'>System</span>
  }
  const user = usersById[userId]
  return <span className='text-break'>{user ? getUserDisplayName(user, userId) : userId}</span>
}

const BillingMetricsRecordDetail: React.FC<{ record: BillingMetricsRecord }> = ({ record }) => (
  <div className='mt-2 p-3 bg-white rounded zocks-border-0.5'>
    <Row className='g-2 mb-2'>
      <Col xs={12} sm={6} md={4}>
        <div className='small fw-semibold text-muted'>Type</div>
        <div className='small'>{humanizeBmrType(record.type)}</div>
      </Col>
      <Col xs={12} sm={6} md={4}>
        <div className='small fw-semibold text-muted'>Created</div>
        <div className='small'>
          <FormattedDate date={record.createdAt} />
        </div>
      </Col>
      <Col xs={12} sm={6} md={4}>
        <div className='small fw-semibold text-muted'>User</div>
        <div className='small'>
          <UserDisplay userId={record.userId} />
        </div>
      </Col>
    </Row>
    <div>
      <div className='small fw-semibold text-muted mb-1'>Context</div>
      <ContextDisplay context={record.context} />
    </div>
  </div>
)

const hasMetadata = (row: LedgerEntry) => Object.keys(row.metadata).length > 0

const LedgerMetadataRow: React.FC<{ data: LedgerEntry }> = ({ data: entry }) => {
  const accountId = useAppSelector(selectAccountId)
  const [triggerGetBillingMetricsRecord] = useLazyGetBillingMetricsRecordQuery()
  const [metricsRecord, setMetricsRecord] = useState<BillingMetricsRecord | null>(null)
  const [metricsLoading, setMetricsLoading] = useState(false)

  const billingMetricsRecordId = typeof entry.metadata.billingMetricsRecordId === 'string' ? entry.metadata.billingMetricsRecordId : null

  useEffect(() => {
    if (!billingMetricsRecordId) {
      return
    }
    let cancelled = false
    const fetchRecord = async () => {
      setMetricsLoading(true)
      try {
        const result = await triggerGetBillingMetricsRecord({ accountId, billingMetricsRecordId }).unwrap()
        if (!cancelled) {
          setMetricsRecord(result.data)
        }
      } catch {
        // error handled by global error middleware
      } finally {
        if (!cancelled) {
          setMetricsLoading(false)
        }
      }
    }
    fetchRecord()
    return () => {
      cancelled = true
    }
  }, [accountId, billingMetricsRecordId, triggerGetBillingMetricsRecord])

  if (!hasMetadata(entry)) {
    return null
  }

  const otherMetadata = Object.fromEntries(Object.entries(entry.metadata).filter(([key]) => key !== 'billingMetricsRecordId'))
  const hasOtherMetadata = Object.keys(otherMetadata).length > 0

  return (
    <div className='px-4 py-2 bg-light border-bottom'>
      {hasOtherMetadata ? (
        <div className='mb-2'>
          <div className='small fw-semibold text-muted mb-1'>Metadata</div>
          <ContextDisplay context={otherMetadata} />
        </div>
      ) : null}
      {billingMetricsRecordId ? (
        <div>
          <div className='small fw-semibold text-muted mb-1'>Billing metrics record</div>
          {metricsLoading ? <Spinner size='sm' /> : metricsRecord ? <BillingMetricsRecordDetail record={metricsRecord} /> : null}
        </div>
      ) : null}
    </div>
  )
}

interface AutoTopUpConfirmModalProps {
  show: boolean
  onHide: () => void
  onConfirm: () => void
  threshold: number
  amount: number
  tiers: PaidCreditTier[] | null
  loading: boolean
  paymentType: 'autoCharge' | 'emailInvoice' | null
}

const AutoTopUpConfirmModal: React.FC<AutoTopUpConfirmModalProps> = ({ show, onHide, onConfirm, threshold, amount, tiers, loading, paymentType }) => {
  const refillTier = tiers ? tierForQuantity(tiers, amount) : null
  const refillCost = refillTier ? formatCurrencyFromCents(refillTier.price.amount * amount, refillTier.price.currency) : null

  const body = (
    <Container className='p-0'>
      <p className='mb-3'>
        {paymentType === 'emailInvoice' ? (
          <>
            Auto top-up will automatically add credits and issue an invoice for them when your credit balance falls below{' '}
            <strong>{formatCredits(threshold)}</strong> credits.
          </>
        ) : (
          <>
            Auto top-up will automatically charge your default payment method when your credit balance falls below <strong>{formatCredits(threshold)}</strong>{' '}
            credits.
          </>
        )}
      </p>
      <p className='mb-0'>
        Each refill will add <strong>{formatCredits(amount)}</strong> credits
        {refillCost ? (
          <>
            {' '}
            for <strong>{refillCost}</strong>
          </>
        ) : null}
        . Continue?
      </p>
    </Container>
  )

  return (
    <Modal
      show={show}
      onHide={onHide}
      header='Enable auto top-up'
      body={body}
      showFooterActions
      centered
      onFooterPrimaryAction={onConfirm}
      footerPrimaryActionLoading={loading}
      footerPrimaryActionTitle='Enable'
    />
  )
}

interface WalletConfigurationProps {
  config: WalletConfig
  accountId: string
  tiers: PaidCreditTier[] | null
  hasPaymentMethod: boolean
  paymentType: 'autoCharge' | 'emailInvoice' | null
}

const WalletConfiguration: React.FC<WalletConfigurationProps> = ({ config, accountId, tiers, hasPaymentMethod, paymentType }) => {
  const canAutoTopUp = hasPaymentMethod || paymentType === 'emailInvoice'
  const { trackingClient } = useTracking()
  const [updateWalletConfig, { isLoading: isSaving }] = useUpdateWalletConfigMutation()

  const [autoTopUpEnabled, setAutoTopUpEnabled] = useState(config.autoTopUp.enabled)
  const [autoTopUpThreshold, setAutoTopUpThreshold] = useState<string>(config.autoTopUp.threshold?.toString() ?? '')
  const [autoTopUpAmount, setAutoTopUpAmount] = useState<string>(config.autoTopUp.amount?.toString() ?? '')
  const [negativeBalanceAllowed, setNegativeBalanceAllowed] = useState(config.negativeBalance.allowed)
  const [negativeBalanceMagnitude, setNegativeBalanceMagnitude] = useState<string>(
    config.negativeBalance.limit !== null ? Math.abs(config.negativeBalance.limit).toString() : '',
  )
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const initialEnabled = config.autoTopUp.enabled
  const initialThreshold = config.autoTopUp.threshold?.toString() ?? ''
  const initialAmount = config.autoTopUp.amount?.toString() ?? ''
  const initialNegativeBalanceAllowed = config.negativeBalance.allowed
  const initialNegativeBalanceMagnitude = config.negativeBalance.limit !== null ? Math.abs(config.negativeBalance.limit).toString() : ''

  const isDirty =
    autoTopUpEnabled !== initialEnabled ||
    autoTopUpThreshold !== initialThreshold ||
    autoTopUpAmount !== initialAmount ||
    negativeBalanceAllowed !== initialNegativeBalanceAllowed ||
    negativeBalanceMagnitude !== initialNegativeBalanceMagnitude

  useEffect(() => {
    if (!isDirty) {
      setAutoTopUpEnabled(config.autoTopUp.enabled)
      setAutoTopUpThreshold(config.autoTopUp.threshold?.toString() ?? '')
      setAutoTopUpAmount(config.autoTopUp.amount?.toString() ?? '')
      setNegativeBalanceAllowed(config.negativeBalance.allowed)
      setNegativeBalanceMagnitude(config.negativeBalance.limit !== null ? Math.abs(config.negativeBalance.limit).toString() : '')
    }
    // we intentionally only re-sync from server values when the form is clean
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  const thresholdNumber = autoTopUpThreshold ? parseInt(autoTopUpThreshold, 10) : null
  const amountNumber = autoTopUpAmount ? parseInt(autoTopUpAmount, 10) : null
  const negativeBalanceMagnitudeNumber = negativeBalanceMagnitude ? parseInt(negativeBalanceMagnitude, 10) : null

  const enabledFormInvalid =
    autoTopUpEnabled &&
    (thresholdNumber === null || isNaN(thresholdNumber) || thresholdNumber < 1 || amountNumber === null || isNaN(amountNumber) || amountNumber < 1)
  const refillLoopWarning = autoTopUpEnabled && thresholdNumber !== null && amountNumber !== null && thresholdNumber > amountNumber
  const negativeBalanceFormInvalid =
    negativeBalanceAllowed &&
    (negativeBalanceMagnitudeNumber === null ||
      isNaN(negativeBalanceMagnitudeNumber) ||
      negativeBalanceMagnitudeNumber < 1 ||
      negativeBalanceMagnitudeNumber > NEGATIVE_BALANCE_MAX_MAGNITUDE)

  const refillTier = tiers && amountNumber ? tierForQuantity(tiers, amountNumber) : null
  const refillCostLabel = refillTier && amountNumber ? formatCurrencyFromCents(refillTier.price.amount * amountNumber, refillTier.price.currency) : null
  const minimumRefill = tiers ? lowestMinQuantity(tiers) : null
  const refillBelowMinimum = autoTopUpEnabled && minimumRefill !== null && amountNumber !== null && amountNumber < minimumRefill ? minimumRefill : null

  const buildPayload = useCallback(
    (overrides: Partial<WalletConfig['autoTopUp']> = {}): WalletConfig => ({
      negativeBalance: {
        allowed: negativeBalanceAllowed,
        limit: negativeBalanceAllowed && negativeBalanceMagnitudeNumber !== null ? -negativeBalanceMagnitudeNumber : null,
      },
      postPaidOverage: config.postPaidOverage,
      autoTopUp: {
        enabled: autoTopUpEnabled,
        threshold: thresholdNumber,
        amount: amountNumber,
        ...overrides,
      },
    }),
    [config.postPaidOverage, negativeBalanceAllowed, negativeBalanceMagnitudeNumber, autoTopUpEnabled, thresholdNumber, amountNumber],
  )

  const _persist = useCallback(
    async (payload: WalletConfig, eventName: TrackEvent) => {
      await updateWalletConfig({ accountId, walletType: CROSS_CLIENT_QUERY_WALLET, payload: { config: payload } }).unwrap()
      trackingClient.track({ eventName })
    },
    [accountId, trackingClient, updateWalletConfig],
  )

  const handleSaveClick = useCallback(() => {
    if (autoTopUpEnabled && !initialEnabled) {
      setShowConfirmModal(true)
      return
    }
    const payload = buildPayload()
    void _persist(payload, autoTopUpEnabled === initialEnabled ? TrackEvent.WALLET_CONFIG_SAVED : TrackEvent.WALLET_AUTO_TOPUP_DISABLED)
  }, [autoTopUpEnabled, initialEnabled, buildPayload, _persist])

  const handleConfirmEnable = useCallback(async () => {
    const payload = buildPayload()
    await _persist(payload, TrackEvent.WALLET_AUTO_TOPUP_ENABLED)
    setShowConfirmModal(false)
  }, [buildPayload, _persist])

  const handleReset = useCallback(() => {
    setAutoTopUpEnabled(initialEnabled)
    setAutoTopUpThreshold(initialThreshold)
    setAutoTopUpAmount(initialAmount)
    setNegativeBalanceAllowed(initialNegativeBalanceAllowed)
    setNegativeBalanceMagnitude(initialNegativeBalanceMagnitude)
  }, [initialEnabled, initialThreshold, initialAmount, initialNegativeBalanceAllowed, initialNegativeBalanceMagnitude])

  const saveDisabled = !isDirty || enabledFormInvalid || negativeBalanceFormInvalid

  return (
    <>
      <AutoTopUpConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmEnable}
        threshold={thresholdNumber ?? 0}
        amount={amountNumber ?? 0}
        tiers={tiers}
        loading={isSaving}
        paymentType={paymentType}
      />
      <Row className='mb-4 align-items-center'>
        <Col>
          <h5 className='mb-0'>Wallet settings</h5>
        </Col>
        <Col xs='auto' className='d-flex gap-2'>
          <Button variant='secondary' onClick={handleReset} disabled={!isDirty || isSaving}>
            Reset
          </Button>
          <Button variant='primary' onClick={handleSaveClick} disabled={saveDisabled} loading={isSaving}>
            Save
          </Button>
        </Col>
      </Row>
      <Row className='mb-3'>
        <Col>
          <div className='p-4 zocks-border-0.5 rounded'>
            <div className='d-flex align-items-start justify-content-between gap-3 mb-3'>
              <div>
                <div className='fw-semibold'>Enable auto top-up</div>
                <div className='small text-muted'>Automatically refill your credit balance when it runs low.</div>
              </div>
              <OverlayTrigger
                placement='left'
                overlay={
                  <Tooltip>
                    {paymentType === 'emailInvoice'
                      ? 'When your balance falls below the threshold we will add the configured number of credits to your wallet and issue an invoice for them.'
                      : 'When your balance falls below the threshold we will charge your default payment method and add the configured number of credits to your wallet.'}
                  </Tooltip>
                }
              >
                <Form.Check type='switch' id='autoTopUpEnabled' checked={autoTopUpEnabled} disabled onChange={(e) => setAutoTopUpEnabled(e.target.checked)} />
              </OverlayTrigger>
            </div>
            {!canAutoTopUp ? (
              <div className='zocks-bg-color-warning p-3 zocks-border-0.5 rounded mb-3'>Add a payment method on the Billing tab to enable auto top-up.</div>
            ) : null}
            {autoTopUpEnabled ? (
              <Row className='g-3'>
                <Col md={6}>
                  <Form.Group controlId='autoTopUpThreshold'>
                    <Form.Label>Refill when balance falls below</Form.Label>
                    <div className='input-group'>
                      <Form.Control
                        type='number'
                        min={1}
                        placeholder='e.g. 100'
                        value={autoTopUpThreshold}
                        onChange={(e) => setAutoTopUpThreshold(e.target.value)}
                        isInvalid={autoTopUpEnabled && (thresholdNumber === null || isNaN(thresholdNumber) || thresholdNumber < 1)}
                        disabled
                      />
                      <span className='input-group-text'>credits</span>
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId='autoTopUpAmount'>
                    <Form.Label>Refill with</Form.Label>
                    <div className='input-group'>
                      <Form.Control
                        type='number'
                        min={1}
                        placeholder='e.g. 1000'
                        value={autoTopUpAmount}
                        onChange={(e) => setAutoTopUpAmount(e.target.value)}
                        isInvalid={autoTopUpEnabled && (amountNumber === null || isNaN(amountNumber) || amountNumber < 1)}
                        disabled
                      />
                      <span className='input-group-text'>credits</span>
                    </div>
                    {refillCostLabel ? <Form.Text className='text-muted'>Each refill will cost {refillCostLabel}.</Form.Text> : null}
                  </Form.Group>
                </Col>
                {refillLoopWarning ? (
                  <Col xs={12}>
                    <div className='zocks-bg-color-warning p-2 zocks-border-0.5 rounded small'>
                      Refill amount is smaller than the threshold — your balance will stay below the threshold and trigger refills repeatedly.
                    </div>
                  </Col>
                ) : null}
                {refillBelowMinimum !== null ? (
                  <Col xs={12}>
                    <div className='zocks-bg-color-warning p-2 zocks-border-0.5 rounded small'>
                      The minimum purchase is {formatCredits(refillBelowMinimum)} credits — refills below this amount will be skipped.
                    </div>
                  </Col>
                ) : null}
              </Row>
            ) : null}
          </div>
        </Col>
      </Row>
      <Row className='mb-5'>
        <Col>
          <div className='p-4 zocks-border-0.5 rounded'>
            <div className='d-flex align-items-start justify-content-between gap-3 mb-3'>
              <div>
                <div className='fw-semibold'>Allow negative balance</div>
                <div className='small text-muted'>Keep Zocks working for a while after your credits run out.</div>
              </div>
              <OverlayTrigger
                placement='left'
                overlay={
                  <Tooltip>
                    When your balance reaches zero, usage can continue until it falls to the configured limit. Purchased credits first cover the negative
                    balance.
                  </Tooltip>
                }
              >
                <Form.Check
                  type='switch'
                  id='negativeBalanceAllowed'
                  checked={negativeBalanceAllowed}
                  disabled
                  onChange={(e) => setNegativeBalanceAllowed(e.target.checked)}
                />
              </OverlayTrigger>
            </div>
            {negativeBalanceAllowed ? (
              <Row className='g-3'>
                <Col md={6}>
                  <Form.Group controlId='negativeBalanceLimit'>
                    <Form.Label>Allow balance to go down to</Form.Label>
                    <div className='input-group'>
                      <span className='input-group-text'>−</span>
                      <Form.Control
                        type='number'
                        min={1}
                        max={NEGATIVE_BALANCE_MAX_MAGNITUDE}
                        placeholder='e.g. 100'
                        value={negativeBalanceMagnitude}
                        onChange={(e) => setNegativeBalanceMagnitude(e.target.value)}
                        isInvalid={negativeBalanceFormInvalid}
                        disabled
                      />
                      <span className='input-group-text'>credits</span>
                    </div>
                    <Form.Text className='text-muted'>At most {formatCredits(NEGATIVE_BALANCE_MAX_MAGNITUDE)} credits.</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            ) : null}
          </div>
        </Col>
      </Row>
    </>
  )
}

interface BalanceCardProps {
  label: string
  value: number
  tooltip: string
}

const BalanceCard: React.FC<BalanceCardProps> = ({ label, value, tooltip }) => (
  <div className='p-3 zocks-border-0.5 rounded h-100'>
    <div className='d-flex align-items-center gap-1'>
      <div className='text-muted small'>{label}</div>
      <OverlayTrigger placement='top' overlay={<Tooltip>{tooltip}</Tooltip>}>
        <i className='bi bi-info-circle text-muted small' />
      </OverlayTrigger>
    </div>
    <div className='fs-4 fw-bold'>{formatCredits(value)}</div>
  </div>
)

interface WalletTabProps {
  creditConfig: CreditConfig | null
}

export const WalletTab: React.FC<WalletTabProps> = ({ creditConfig }) => {
  const accountId = useAppSelector(selectAccountId)
  const { tracked } = useTracking()
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)

  const tiers = creditConfig?.paid?.tiers ?? null

  const { data: customer } = useGetBillingCustomerQuery({ accountId })
  const hasPaymentMethod = !!customer?.data.hasPaymentMethod
  const paymentType = customer?.data.subscription?.paymentType ?? null

  const { data: wallet, isLoading: walletLoading } = useGetWalletQuery(
    { accountId, walletType: CROSS_CLIENT_QUERY_WALLET },
    { pollingInterval: POLLING_INTERVAL },
  )

  const { data: ledger, isLoading: ledgerLoading } = useGetLedgerQuery(
    { accountId, walletType: CROSS_CLIENT_QUERY_WALLET, pageRequest: { page: page - 1, size: perPage } },
    { pollingInterval: POLLING_INTERVAL },
  )

  const { data: lots } = useGetLotsQuery({ accountId, walletType: CROSS_CLIENT_QUERY_WALLET }, { pollingInterval: POLLING_INTERVAL })

  const soonestExpiringLot = useMemo(() => (lots?.data ?? []).find((lot) => lot.expiresAt !== null) ?? null, [lots])

  const userIdsOnPage = useMemo(() => {
    const ids = new Set<string>()
    for (const entry of ledger?.data ?? []) {
      if (entry.userId && entry.userId !== SERVICE_TOKEN_USER_ID) {
        ids.add(entry.userId)
      }
    }
    return Array.from(ids)
  }, [ledger?.data])

  const { data: usersResponse } = useGetAllUsersQuery({ accountId, ids: userIdsOnPage }, { skip: userIdsOnPage.length === 0 })

  const usersById = useMemo(() => {
    const map: Record<string, User> = {}
    for (const u of usersResponse?.data ?? []) {
      map[u.id] = u
    }
    return map
  }, [usersResponse])

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  const handlePerRowsChange = useCallback((newPerPage: number, newPage: number) => {
    setPage(newPage)
    setPerPage(newPerPage)
  }, [])

  const handleOpenPurchaseModal = tracked(() => setShowPurchaseModal(true), {
    eventName: TrackEvent.WALLET_PURCHASE_OPENED,
    eventDetails: { source: 'walletTab' },
  })

  const columns: TableColumn<LedgerEntry>[] = useMemo(
    () => [
      {
        name: 'Date',
        sortable: true,
        cell: (row) => <FormattedDate date={row.createdAt} />,
      },
      {
        name: 'Type',
        sortable: true,
        cell: (row) => {
          const known = LEDGER_TYPE_LABELS[row.type]
          return <span>{known ? known.label : humanizeKey(row.type)}</span>
        },
      },
      {
        name: 'Amount',
        sortable: true,
        right: true,
        cell: (row) => {
          const tone = LEDGER_TYPE_LABELS[row.type]?.tone ?? (row.amount < 0 ? 'debit' : 'credit')
          const sign = row.amount > 0 ? '+' : ''
          return (
            <span className={classNames('fw-semibold', { 'text-success': tone === 'credit', 'text-muted': tone === 'debit' })}>
              {sign}
              {formatCredits(row.amount)}
            </span>
          )
        },
      },
      {
        name: 'User',
        cell: (row) => <UserDisplay userId={row.userId} />,
      },
    ],
    [],
  )

  return (
    <UsersByIdContext.Provider value={usersById}>
      <Container className='p-0'>
        <PurchaseCreditsModal
          show={showPurchaseModal}
          onHide={() => setShowPurchaseModal(false)}
          tiers={tiers}
          paymentType={paymentType}
          hasPaymentMethod={hasPaymentMethod}
          source='walletTab'
        />
        <Row className='mb-4 align-items-center'>
          <Col>
            <h5 className='mb-0'>Credit balance</h5>
          </Col>
          <Col xs='auto'>
            <Button variant='primary' onClick={handleOpenPurchaseModal}>
              <i className='bi bi-plus' /> Purchase credits
            </Button>
          </Col>
        </Row>
        {walletLoading ? (
          <Row className='mb-4 justify-content-center'>
            <Spinner />
          </Row>
        ) : wallet?.data ? (
          <>
            <Row className={classNames('g-3', soonestExpiringLot ? 'mb-2' : 'mb-5')}>
              <Col md={4}>
                <BalanceCard label='Credit balance' value={wallet.data.totalCoinBalance} tooltip='Credits available in your wallet.' />
              </Col>
            </Row>
            {soonestExpiringLot?.expiresAt ? (
              <Row className='mb-5'>
                <Col>
                  <div className='small text-muted'>
                    <i className='bi bi-hourglass-split me-1' />
                    {formatCredits(soonestExpiringLot.amount)} credits expire on <FormattedDate date={soonestExpiringLot.expiresAt} />
                  </div>
                </Col>
              </Row>
            ) : null}
          </>
        ) : null}
        {wallet?.data?.config ? (
          <WalletConfiguration config={wallet.data.config} accountId={accountId} tiers={tiers} hasPaymentMethod={hasPaymentMethod} paymentType={paymentType} />
        ) : null}
        <Row className='mb-3'>
          <Col>
            <h5 className='mb-0'>Ledger</h5>
          </Col>
        </Row>
        <Row>
          <Col>
            <ZocksDataTable
              keyField='id'
              columns={columns}
              data={ledger?.data ?? []}
              progressPending={ledgerLoading}
              pagination
              paginationServer
              paginationTotalRows={ledger?.page.totalElements}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              expandableRows
              expandableRowDisabled={(row) => !hasMetadata(row)}
              expandableRowsComponent={LedgerMetadataRow}
              noDataComponent={
                <div className='py-5 text-center text-muted'>
                  <i className='bi bi-inbox fs-3 d-block mb-2' />
                  No transactions yet
                </div>
              }
            />
          </Col>
        </Row>
      </Container>
    </UsersByIdContext.Provider>
  )
}
